import { type FormEvent, useState } from 'react';
import toast from 'react-hot-toast';

import { CardElement } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';

import validateTaxId from '~/utils/validateTaxId';

interface Props {
  stripe: Stripe | null;
  elements: any;
  email: string;
  fetcher: any;
  setEmail: (email: string) => void;
}

interface ReturnProps {
  handleDonationAmountCentsChanged: (value: string) => void;
  handleTaxIdChange: (value: string) => { isValid: boolean; type?: string };
  taxIdValue?: string;
  taxIdIsValid: boolean;
  handleSubmit: (event: FormEvent) => Promise<void>;
  submissionTriggered: boolean;
  handleRemoveDisabledState: () => void;
}

const useInternalCheckout = ({ stripe, elements, email, fetcher, setEmail }: Props): ReturnProps => {
  const [taxIdValue, setTaxIdValue] = useState<undefined | string>(undefined);
  const [taxIdType, setTaxIdType] = useState<undefined | string>(undefined);
  const [taxIdIsValid, setTaxIdIsValid] = useState(true);
  const [submissionTriggered, setSubmissionTriggered] = useState(false);
  const [donationAmountCents, setDonationAmountCents] = useState('');

  const handleTaxIdChange = (value: string) => {
    const validation = validateTaxId(value);

    setTaxIdValue(value);
    setTaxIdType(validation.type);
    setTaxIdIsValid(validation.isValid);

    return validation;
  };

  const handleRemoveDisabledState = () => {
    setSubmissionTriggered(false);
  };

  const handleDonationAmountCentsChanged = (value: string) => {
    setDonationAmountCents(value);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!email) {
      toast.error('Email is required');
      handleRemoveDisabledState();
      return;
    }

    if (taxIdValue && !taxIdIsValid) {
      toast.error('Invalid tax ID');
      handleRemoveDisabledState();
      return;
    }

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      return;
    }

    if (submissionTriggered) {
      toast.loading('Submission in process...');
      return;
    }

    setSubmissionTriggered(true);
    const card = elements.getElement(CardElement);
    const result = await stripe.createToken(card as any);

    const paymentMethod = await stripe?.createPaymentMethod({
      type: 'card',
      // biome-ignore lint/style/noNonNullAssertion: Keeping for backwards compatibility
      card: elements.getElement(CardElement)!,
      billing_details: {
        email,
      },
    });

    if (result.error) {
      console.log(result.error.message);
    } else {
      const formData = new FormData(event.target as HTMLFormElement);
      formData.append('payment_method', paymentMethod?.paymentMethod?.id || '');
      formData.append('email', email);
      formData.append('tax_id', taxIdValue || '');
      formData.append('tax_id_type', taxIdType || '');
      formData.append('amount_cents', donationAmountCents);
      fetcher.submit(formData, { method: 'post', action: '/upgrade' });
      setEmail(email);
    }
  };

  return {
    taxIdValue,
    taxIdIsValid,
    handleDonationAmountCentsChanged,
    handleTaxIdChange,
    handleSubmit,
    submissionTriggered,
    handleRemoveDisabledState,
  };
};

export default useInternalCheckout;
